/**
 * Headless table with pagination and a search box.
 *
 * Hand-rolled rather than pulling in a Refine UI kit. The project has
 * `@refinedev/core` only — no antd, no MUI — and that is the right call for a
 * console with four resources: a UI kit is a large dependency, a design language to
 * fight, and a second way to lay out a form. Refine's headless hooks give the data
 * plumbing; this gives the markup, in the same class vocabulary as styles.css.
 */

import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  /** Right-aligned via the `num` class. Use for money and counts. */
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  /** Omit to hide the pager entirely (for short, unpaginated lists). */
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  pagination,
  onRowClick,
  emptyMessage = 'Nothing here yet.',
}: DataTableProps<T>) {
  if (error) {
    return (
      <p className="alert alert--error">
        {error instanceof Error ? error.message : 'Could not load this list.'}
      </p>
    );
  }

  // Distinguished from "no rows" deliberately. An empty table that says "Nothing
  // here yet" while a request is still in flight reads as a data problem, and under
  // RLS "no rows" is a legitimate and meaningful answer — a tenant genuinely sees
  // nothing on a staff list. Conflating the two would make a permission boundary
  // look like a bug.
  if (isLoading) return <p className="muted">Loading…</p>;

  const lastPage = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1;

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.numeric ? 'num' : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="muted">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((column) => (
                  <td key={column.key} className={column.numeric ? 'num' : undefined}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {pagination && pagination.total > pagination.pageSize ? (
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={pagination.page <= 1}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
          >
            ← Previous
          </button>
          <span className="muted">
            Page {pagination.page} of {lastPage} · {pagination.total} total
          </span>
          <button
            type="button"
            className="button button--quiet"
            disabled={pagination.page >= lastPage}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
          >
            Next →
          </button>
        </div>
      ) : null}
    </>
  );
}
